import { useNavigate } from "react-router-dom";
import { useSearchContext } from "../context/SearchContext";
import { SearchForm } from "../components/SearchForm";

const POPULAR = [
  "Taylor Swift",
  "Kendrick Lamar",
  "Olivia Rodrigo",
  "Bad Bunny",
  "Beyoncé",
  "The Weeknd",
];

const STEPS = [
  {
    n: "1",
    title: "Search your artist",
    body: "Type any artist and your home airport. We pull every upcoming US tour date.",
  },
  {
    n: "2",
    title: "We price the whole trip",
    body: "For each city we add up the concert ticket, round-trip flight, and 2-night hotel.",
  },
  {
    n: "3",
    title: "Book the cheapest city",
    body: "Compare all-in cost across cities, pick your package, and book in a couple of taps.",
  },
];

export function HomePage() {
  const navigate = useNavigate();
  const { dispatch } = useSearchContext();

  function pickArtist(artist: string): void {
    dispatch({ type: "SET_ARTIST", artist });
    navigate("/search");
  }

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-b from-brand-900 via-brand-900 to-slate-900">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-20 [background:radial-gradient(60rem_30rem_at_50%_-10%,#a78bfa,transparent)]"
        />
        <div className="relative mx-auto max-w-3xl px-4 pb-16 pt-16 text-center md:pt-20">
          <h1 className="text-4xl font-extrabold tracking-tight text-white md:text-5xl">
            See your favorite artist
            <br />
            for less — in any city.
          </h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-brand-100">
            ConcertDeals prices the entire trip — ticket&nbsp;+&nbsp;flight&nbsp;+&nbsp;hotel —
            and ranks every tour stop by total all-in cost.
          </p>
        </div>

        {/* Search card overlaps the hero/content boundary */}
        <div className="relative mx-auto -mb-12 max-w-2xl translate-y-12 px-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-xl">
            <SearchForm
              ctaLabel="Find the cheapest city"
              onSubmitted={() => navigate("/search")}
            />
            <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
              <span className="text-xs text-slate-400">Popular:</span>
              {POPULAR.map((a) => (
                <button
                  key={a}
                  type="button"
                  onClick={() => pickArtist(a)}
                  className="rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
                >
                  {a}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="mx-auto max-w-5xl px-4 pt-24">
        <h2 className="text-center text-2xl font-bold text-slate-900">
          One search. Every city. Total cost.
        </h2>
        <p className="mx-auto mt-2 max-w-2xl text-center text-slate-500">
          No more juggling five tabs to compare ticket sites, flight search, and
          hotels. We do the math for every tour stop at once.
        </p>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {STEPS.map((s) => (
            <div
              key={s.n}
              className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              <div className="grid h-10 w-10 place-items-center rounded-full bg-brand-600 text-base font-bold text-white">
                {s.n}
              </div>
              <h3 className="mt-4 text-lg font-semibold text-slate-900">
                {s.title}
              </h3>
              <p className="mt-2 text-sm text-slate-500">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Value props */}
      <section className="mx-auto max-w-5xl px-4 pt-16">
        <div className="grid gap-6 rounded-2xl bg-slate-900 p-8 text-white md:grid-cols-3">
          <div>
            <div className="text-3xl font-extrabold text-brand-300">3 packages</div>
            <p className="mt-1 text-sm text-slate-300">
              Cheapest, Best Value, and Premium for every show — pick the
              trade-off that fits.
            </p>
          </div>
          <div>
            <div className="text-3xl font-extrabold text-brand-300">All-in</div>
            <p className="mt-1 text-sm text-slate-300">
              Every price includes ticket, round-trip flight, and a 2-night
              hotel near the venue.
            </p>
          </div>
          <div>
            <div className="text-3xl font-extrabold text-brand-300">Free</div>
            <p className="mt-1 text-sm text-slate-300">
              We earn a commission from booking partners, so the search is
              always free for you.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-3xl px-4 pb-8 pt-16 text-center">
        <h2 className="text-2xl font-bold text-slate-900">
          Ready to find your show?
        </h2>
        <p className="mt-2 text-slate-500">
          Pick an artist and your home airport — we&apos;ll handle the rest.
        </p>
        <button
          type="button"
          onClick={() => navigate("/search")}
          className="mt-6 rounded-lg bg-brand-600 px-6 py-3 text-base font-semibold text-white transition hover:bg-brand-700"
        >
          Start searching
        </button>
      </section>
    </div>
  );
}
